import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge, Button, Checkbox, Dialog, DialogActions, DialogBody, DialogContent, DialogSurface, DialogTitle, Dropdown, Field, Input, MessageBar, MessageBarBody,
  Option, Spinner, Switch, Table, TableBody, TableCell, TableHeader,
  TableHeaderCell, TableRow, Text, makeStyles, tokens,
} from '@fluentui/react-components';
import { ApiClientError, signInAccessService, type DeniedSignInAttempt, type SignInAccessRule } from '../api';
import AdminPageLayout, { AdminPageContent } from '../components/AdminPageLayout';
import { AdminDataGrid, AdminDataGridEmpty, AdminPagination } from '../components/AdminDataGrid';
import { useTopBarBreadcrumbs, useTopBarTitle } from '../contexts/TopBarContext';

const PAGE_SIZE = 50;
const useStyles = makeStyles({
  policy: { marginBottom: '20px' },
  description: { display: 'block', margin: '8px 0 20px', color: tokens.colorNeutralForeground3 },
  form: { display: 'flex', alignItems: 'end', gap: '12px', marginBottom: '20px', flexWrap: 'wrap' },
  value: { minWidth: '300px' },
  message: { marginBottom: '16px' },
  reviewSection: { marginBottom: '28px' },
  sectionTitle: { display: 'block', marginBottom: '4px' },
});

function ruleStatus(rule: SignInAccessRule): { label: string; color: 'success' | 'danger' | 'warning' | 'informative' } {
  if (rule.claimedAt) return { label: 'Used', color: 'success' };
  if (rule.expiresAt && new Date(rule.expiresAt) <= new Date()) return { label: 'Expired', color: 'warning' };
  return { label: rule.type === 'domain' ? 'Active · reusable' : 'Active', color: 'informative' };
}

const AdminSignInAccessPage: React.FC = () => {
  const styles = useStyles();
  const [rules, setRules] = useState<SignInAccessRule[]>([]);
  const [deniedAttempts, setDeniedAttempts] = useState<DeniedSignInAttempt[]>([]);
  const [deniedTotal, setDeniedTotal] = useState(0);
  const [deniedPage, setDeniedPage] = useState(1);
  const [deniedLoading, setDeniedLoading] = useState(true);
  const [authorizingId, setAuthorizingId] = useState<string>();
  const [invitationRequired, setInvitationRequired] = useState(false);
  const [type, setType] = useState<'email' | 'domain'>('email');
  const [value, setValue] = useState('');
  const [hasExpiry, setHasExpiry] = useState(false);
  const [expiresAt, setExpiresAt] = useState('');
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();
  const [deleteCandidate, setDeleteCandidate] = useState<SignInAccessRule>();
  const [deleting, setDeleting] = useState(false);
  useTopBarTitle('Sign-in Access');
  useTopBarBreadcrumbs([{ text: 'Admin' }, { text: 'Sign-in Access' }]);

  const load = useCallback(async () => {
    setLoading(true); setError(undefined);
    try {
      const result = await signInAccessService.list(page, PAGE_SIZE);
      setRules(result.rules); setTotal(result.total); setInvitationRequired(result.invitationRequired);
    } catch (loadError) {
      setError(loadError instanceof ApiClientError ? loadError.error : 'Sign-in access settings could not be loaded.');
    } finally { setLoading(false); }
  }, [page]);
  useEffect(() => { void load(); }, [load]);

  const loadDeniedAttempts = useCallback(async () => {
    setDeniedLoading(true); setError(undefined);
    try {
      const result = await signInAccessService.listDeniedAttempts(deniedPage, PAGE_SIZE);
      setDeniedAttempts(result.attempts); setDeniedTotal(result.total);
    } catch (loadError) {
      setError(loadError instanceof ApiClientError ? loadError.error : 'Denied sign-in attempts could not be loaded.');
    } finally { setDeniedLoading(false); }
  }, [deniedPage]);
  useEffect(() => { void loadDeniedAttempts(); }, [loadDeniedAttempts]);

  const updatePolicy = async (required: boolean) => {
    setSaving(true); setError(undefined);
    try {
      const result = await signInAccessService.updateSettings(required);
      setInvitationRequired(result.invitationRequired);
    } catch (updateError) {
      setError(updateError instanceof ApiClientError ? updateError.error : 'The sign-in policy could not be updated.');
    } finally { setSaving(false); }
  };

  const createRule = async (event: React.FormEvent) => {
    event.preventDefault(); setSaving(true); setError(undefined);
    try {
      await signInAccessService.createRule(type, value, hasExpiry ? new Date(expiresAt).toISOString() : null);
      setValue(''); setExpiresAt(''); setHasExpiry(false);
      if (page === 1) await load(); else setPage(1);
    } catch (createError) {
      setError(createError instanceof ApiClientError ? createError.error : 'The access rule could not be created.');
    } finally { setSaving(false); }
  };

  const deleteRule = async () => {
    if (!deleteCandidate) return;
    setDeleting(true); setError(undefined);
    try { await signInAccessService.deleteRule(deleteCandidate.id); setDeleteCandidate(undefined); await load(); }
    catch (deleteError) { setError(deleteError instanceof ApiClientError ? deleteError.error : 'The access rule could not be deleted.'); }
    finally { setDeleting(false); }
  };

  const authorize = async (attempt: DeniedSignInAttempt) => {
    setAuthorizingId(attempt.id); setError(undefined);
    try {
      await signInAccessService.authorizeDeniedAttempt(attempt.id);
      await Promise.all([load(), loadDeniedAttempts()]);
    } catch (authorizeError) {
      setError(authorizeError instanceof ApiClientError ? authorizeError.error : 'The email address could not be authorized.');
    } finally { setAuthorizingId(undefined); }
  };

  return (
    <AdminPageLayout>
      <AdminPageContent>
      <section className={styles.policy}>
        <Switch
          label="Require an invitation for new users"
          checked={invitationRequired}
          disabled={saving || loading}
          onChange={(_, data) => void updatePolicy(data.checked)}
        />
        <Text className={styles.description}>Existing linked users remain unaffected. Email invitations are single-use; domain rules are reusable.</Text>
      </section>
      <form className={styles.form} onSubmit={createRule}>
        <Field label="Rule type">
          <Dropdown value={type === 'email' ? 'Email address' : 'Domain'} selectedOptions={[type]} onOptionSelect={(_, data) => setType(data.optionValue as 'email' | 'domain')}>
            <Option value="email">Email address</Option><Option value="domain">Domain</Option>
          </Dropdown>
        </Field>
        <Field className={styles.value} label={type === 'email' ? 'Email address' : 'Domain'} required>
          <Input value={value} maxLength={320} placeholder={type === 'email' ? 'person@example.com' : 'example.com'} onChange={(_, data) => setValue(data.value)} required />
        </Field>
        <Checkbox label="Expires" checked={hasExpiry} onChange={(_, data) => setHasExpiry(data.checked === true)} />
        {hasExpiry && <Field label="Expires at" required><Input type="datetime-local" value={expiresAt} onChange={(_, data) => setExpiresAt(data.value)} required /></Field>}
        <Button appearance="primary" type="submit" disabled={saving || !value || (hasExpiry && !expiresAt)}>{saving ? <Spinner size="tiny" /> : 'Add rule'}</Button>
      </form>
      {error && <MessageBar className={styles.message} intent="error"><MessageBarBody>{error}</MessageBarBody></MessageBar>}
      <section className={styles.reviewSection}>
        <Text as="h2" size={500} weight="semibold" className={styles.sectionTitle}>Denied sign-in attempts</Text>
        <Text className={styles.description}>Verified external identities denied for missing access. Authorizing creates an exact-email invitation.</Text>
        {deniedLoading && <Spinner size="small" label="Loading denied sign-in attempts" />}
        <AdminDataGrid><Table aria-label="Denied external sign-in attempts">
          <TableHeader><TableRow><TableHeaderCell>Email</TableHeaderCell><TableHeaderCell>Provider</TableHeaderCell><TableHeaderCell>Attempts</TableHeaderCell><TableHeaderCell>First attempt</TableHeaderCell><TableHeaderCell>Last attempt</TableHeaderCell><TableHeaderCell>Action</TableHeaderCell></TableRow></TableHeader>
          <TableBody>{deniedAttempts.map((attempt) => <TableRow key={attempt.id}>
            <TableCell>{attempt.email}</TableCell><TableCell><Badge>{attempt.provider}</Badge></TableCell><TableCell>{attempt.attemptCount}</TableCell>
            <TableCell>{new Date(attempt.firstAttemptAt).toLocaleString()}</TableCell><TableCell>{new Date(attempt.lastAttemptAt).toLocaleString()}</TableCell>
            <TableCell><Button appearance="primary" disabled={authorizingId !== undefined} onClick={() => void authorize(attempt)}>{authorizingId === attempt.id ? <Spinner size="tiny" /> : 'Authorize email'}</Button></TableCell>
          </TableRow>)}</TableBody>
        </Table></AdminDataGrid>
        {!deniedLoading && deniedAttempts.length === 0 && <AdminDataGridEmpty>No denied sign-in attempts require review.</AdminDataGridEmpty>}
        <AdminPagination totalLabel={`${deniedTotal} denied attempts`} page={deniedPage} pageSize={PAGE_SIZE} total={deniedTotal} loading={deniedLoading} onPageChange={setDeniedPage} />
      </section>
      <Text as="h2" size={500} weight="semibold" className={styles.sectionTitle}>Access rules</Text>
      {loading && <Spinner size="small" label="Loading sign-in access rules" />}
      <AdminDataGrid><Table aria-label="External sign-in access rules">
        <TableHeader><TableRow><TableHeaderCell>Type</TableHeaderCell><TableHeaderCell>Value</TableHeaderCell><TableHeaderCell>Status</TableHeaderCell><TableHeaderCell>Expires</TableHeaderCell><TableHeaderCell>Used by</TableHeaderCell><TableHeaderCell>Action</TableHeaderCell></TableRow></TableHeader>
        <TableBody>{rules.map((rule) => { const status = ruleStatus(rule); return <TableRow key={rule.id}>
          <TableCell>{rule.type}</TableCell><TableCell>{rule.value}</TableCell><TableCell><Badge color={status.color}>{status.label}</Badge></TableCell>
          <TableCell>{rule.expiresAt ? new Date(rule.expiresAt).toLocaleString() : 'Never'}</TableCell>
          <TableCell>{rule.claimedByUser?.email ? `${rule.claimedByUser.email} (${rule.claimedProvider})` : '—'}</TableCell>
          <TableCell><Button appearance="subtle" onClick={() => setDeleteCandidate(rule)}>Delete</Button></TableCell>
        </TableRow>; })}</TableBody>
      </Table></AdminDataGrid>
      {!loading && rules.length === 0 && <AdminDataGridEmpty>No sign-in access rules have been created.</AdminDataGridEmpty>}
      <AdminPagination totalLabel={`${total} rules`} page={page} pageSize={PAGE_SIZE} total={total} loading={loading} onPageChange={setPage} />
      </AdminPageContent>
      <Dialog open={deleteCandidate !== undefined} onOpenChange={(_, data) => !data.open && !deleting && setDeleteCandidate(undefined)}>
        <DialogSurface><DialogBody>
          <DialogTitle>Delete access rule</DialogTitle>
          <DialogContent>Delete the {deleteCandidate?.type} rule for <strong>{deleteCandidate?.value}</strong>? This cannot be undone.</DialogContent>
          <DialogActions>
            <Button appearance="secondary" disabled={deleting} onClick={() => setDeleteCandidate(undefined)}>Cancel</Button>
            <Button appearance="primary" disabled={deleting} onClick={() => void deleteRule()}>{deleting ? <Spinner size="tiny" /> : 'Delete rule'}</Button>
          </DialogActions>
        </DialogBody></DialogSurface>
      </Dialog>
    </AdminPageLayout>
  );
};

export default AdminSignInAccessPage;